import type { SupabaseClient } from "@supabase/supabase-js";
import type { Database } from "@/integrations/supabase/types";

type QueryResult = { data: unknown; error: { message?: string } | null };

export const buildSettingsExportFileName = (workspaceLabel?: string | null) => {
  const slug = (workspaceLabel ?? "workspace")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "") || "workspace";

  return `excerptus-export-${slug}-${new Date().toISOString().slice(0, 10)}.json`;
};

export const triggerJsonDownload = (fileName: string, payload: unknown) => {
  const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = fileName;
  anchor.click();
  URL.revokeObjectURL(url);
};

const getMessage = (result: QueryResult, fallback: string) => result.error?.message ?? fallback;

export const loadWorkspaceExportData = async (
  supabase: SupabaseClient<Database>,
  params: { workspaceId: string; userId: string; environment: string; userEmail?: string | null; workspaceLabel?: string | null; displayName?: string | null; personaId?: string | null },
) => {
  const {
    workspaceId,
    userId,
    environment,
    userEmail = null,
    workspaceLabel = null,
    displayName = null,
    personaId = null,
  } = params;

  // Integrations are scoped to the active Persona (primary profile). If no
  // personaId is provided, fall back to the workspace itself (back-compat
  // for callers that haven't been updated; the row will simply be empty for
  // non-primary workspaces).
  const integrationsProfileId = personaId ?? workspaceId;

  const [tags, libraryItems, libraryCategories, excerpts, brandColors, quoteSettings, readwiseHighlights, usageCounters, integrations, subscriptions] = await Promise.all([
    supabase.from("tags").select("*").eq("workspace_id", workspaceId).order("name"),
    supabase.from("library_items").select("*").eq("workspace_id", workspaceId).order("updated_at", { ascending: false }),
    supabase.from("library_categories").select("*").eq("workspace_id", workspaceId).order("name"),
    supabase.from("excerpts").select("*").eq("workspace_id", workspaceId).order("created_at", { ascending: true }),
    supabase.from("brand_colors").select("*").eq("workspace_id", workspaceId).order("slot_index"),
    supabase.from("excerpt_image_generator_settings").select("*").eq("workspace_id", workspaceId).maybeSingle(),
    supabase.from("readwise_highlights").select("*").eq("workspace_id", workspaceId).order("highlighted_at", { ascending: false }),
    supabase.from("usage_counters").select("*").eq("workspace_id", workspaceId).order("month", { ascending: false }),
    supabase.from("user_integrations").select("provider, status, error_message, last_sync_cursor, last_synced_at, created_at, updated_at").eq("profile_id", integrationsProfileId).order("created_at", { ascending: false }),
    supabase.from("subscriptions").select("status, product_id, price_id, current_period_start, current_period_end, cancel_at_period_end, environment, created_at, updated_at").eq("user_id", userId).eq("environment", environment).order("updated_at", { ascending: false }),
  ]);
  // Topics are now sourced from the Codex Framework Topic Tree
  // (mem://features/topics-are-codex-driven.md); export them from there instead.
  const topics = await supabase
    .from("content_framework_topics")
    .select("*")
    .eq("workspace_id", workspaceId)
    .order("label");

  const excerptIdList = ((excerpts.data as Array<{ id: string }> | null) ?? []).map((entry) => entry.id);
  const libraryItemIdList = ((libraryItems.data as Array<{ id: string }> | null) ?? []).map((entry) => entry.id);

  // Decrypt sensitive fields for the full export so users get plaintext back, not previews.
  // Library items hold all source metadata + decrypted notes/description.
  const [
    decryptedExcerptNotesResult,
    decryptedLibraryResult,
  ] = await Promise.all([
    excerptIdList.length
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ? (supabase as any).rpc("get_decrypted_excerpts", { _workspace_id: workspaceId, _ids: null })
      : Promise.resolve({ data: [], error: null }),
    libraryItemIdList.length
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ? (supabase as any).rpc("get_decrypted_library_items", { _workspace_id: workspaceId, _ids: null })
      : Promise.resolve({ data: [], error: null }),
  ]);

  const excerptNotesById = new Map<string, string | null>(
    ((decryptedExcerptNotesResult.data ?? []) as Array<{ id: string; notes: string | null }>).map((row) => [row.id, row.notes]),
  );
  const libraryTextById = new Map<string, { notes: string | null; description: string | null }>(
    ((decryptedLibraryResult.data ?? []) as Array<{ id: string; notes: string | null; description: string | null }>).map(
      (row) => [row.id, { notes: row.notes, description: row.description }],
    ),
  );

  const excerptsWithNotes = ((excerpts.data as Array<Record<string, unknown>> | null) ?? []).map((row) => ({
    ...row,
    notes: excerptNotesById.get(row.id as string) ?? null,
  }));
  const libraryItemsWithText = ((libraryItems.data as Array<Record<string, unknown>> | null) ?? []).map((row) => {
    const decrypted = libraryTextById.get(row.id as string);
    return {
      ...row,
      notes: decrypted?.notes ?? (row.notes as string | null) ?? null,
      description: decrypted?.description ?? (row.description as string | null) ?? null,
    };
  });

  const [libraryItemCategories, excerptTags, excerptTopics] = await Promise.all([
    libraryItemIdList.length
      ? supabase.from("library_item_categories").select("library_item_id, library_category_id, created_at").in("library_item_id", libraryItemIdList)
      : Promise.resolve({ data: [], error: null }),
    excerptIdList.length
      ? supabase.from("excerpt_tags").select("excerpt_id, tag_id").in("excerpt_id", excerptIdList)
      : Promise.resolve({ data: [], error: null }),
    excerptIdList.length
      ? supabase.from("excerpt_topics").select("excerpt_id, topic_id").in("excerpt_id", excerptIdList)
      : Promise.resolve({ data: [], error: null }),
  ]);

  const results: QueryResult[] = [tags, topics, libraryItems, libraryCategories, libraryItemCategories, excerpts, excerptTags, excerptTopics, brandColors, quoteSettings, readwiseHighlights, usageCounters, integrations, subscriptions];
  const firstError = results.find((result) => result.error);
  if (firstError) throw new Error(getMessage(firstError, "Failed to export workspace data"));

  return {
    exported_at: new Date().toISOString(),
    workspace: {
      id: workspaceId,
      label: workspaceLabel,
      display_name: displayName,
    },
    account: {
      user_id: userId,
      email: userEmail,
      environment,
    },
    data: {
      tags: tags.data ?? [],
      topics: topics.data ?? [],
      library_items: libraryItemsWithText,
      library_categories: libraryCategories.data ?? [],
      library_item_categories: libraryItemCategories.data ?? [],
      excerpts: excerptsWithNotes,
      excerpt_tags: excerptTags.data ?? [],
      excerpt_topics: excerptTopics.data ?? [],
      brand_colors: brandColors.data ?? [],
      excerpt_image_generator_settings: quoteSettings.data,
      readwise_highlights: readwiseHighlights.data ?? [],
      usage_counters: usageCounters.data ?? [],
      user_integrations: integrations.data ?? [],
      subscriptions: subscriptions.data ?? [],
    },
  };
};